import { DateTime } from "luxon";
import { Model, Shift } from "../schema";
import { OT_SHIFT_CODES_KEYS } from "../shiftCodes";

// Utility function to get adjusted day of week for 6am-6am work days
export const getAdjustedDayOfWeek = (date: DateTime): number => {
    // If the time is before 6 AM, we should consider it part of the previous day
    if (date.hour < 6) {
        // Subtract 1 day to get the previous day
        const adjustedDate = date.minus({ days: 1 });
        return adjustedDate.weekday;
    }

    return date.weekday;
};

export const getMonthDateBoundaries = (date: DateTime): { start: DateTime, end: DateTime } => {
    const startOfMonth = date.set({ day: 1, hour: 6, minute: 0, second: 0, millisecond: 0 });
    const endOfMonth = startOfMonth.plus({ months: 1 }).set({ hour: 5, minute: 59, second: 59, millisecond: 999 });

    return { start: startOfMonth, end: endOfMonth };
}

// Hours per shift
export const HOURS_PER_SHIFT = 8.00;
export const HOURS_PER_WEEKEND_SHIFT = 12.00;

// UK (£)
// Weekday pay
export const UK_TAX_PAYER_WEEKDAY_PAY = 2150.00;     // UK Tax Payer (different minimum pay)
export const UK_NON_TAX_PAYER_WEEKDAY_PAY = 2000.00; // UK Non-Tax Payer
export const UK_OT13S_PAY = 13.00;

// Weekend pay
export const UK_AMS_PAY = 1400.00;
export const UK_PMS_PAY = 1500.00;
export const UK_OT15S_PAY = 15.00;

// Other Pay
export const UK_LAUNCH_PRE_17_08_2025_PAY = 15.00;
export const UK_LAUNCH_PAY = 18.00;

// FP ($)
// Weekday pay
export const FP_STANDARD_WEEKDAY_PAY = 3.00;
export const FP_OT4_PAY = 4.00;

// Weekend pay
export const FP_OT_WKND_PAY = 3.60;

export interface ShiftData {
    shift_id: string;
    user_id: string;
    model_id: string;
    date: DateTime;
    shift_type: string;
    hours: number;
    ppv_earned: number;
    noppv_earned: number;
    pay_due: number;
}

export interface SchedulingShiftData {
    shift_id: string;
    user_id: string;
    model_id: string;
    date: DateTime;
    shift_type: string;
    is_ot: boolean;
    pay_due: number;
}

export interface CalculatorReturn {
    nonOvertimePay: number;
    overtimePay: number;
    total: number;
    set_totals: Record<string, number>;
    shifts: ShiftData[];
    setHours: Record<string, {
        regular_hours: number;
        weekend_ot_hours: number;
        weekday_ot_hours: number;
        regular_pay: number;
        weekend_ot_pay: number;
        weekday_ot_pay: number;
        total_regular_ppv: number;
        total_ot_ppv: number;
        nonppv: number;
    }>;
}

export interface PayCalculatorResult {
    total_pay: number;
    weekdayPay: number;
    weekendPay: number;
    overtimePay: number;
    nonOvertimePay: number;
    is_approximation: boolean;
    set_totals: Record<string, {
        total: number;
        regular_hours: number;  // Non-overtime hours
        weekend_ot_hours: number;  // Weekend overtime hours
        weekday_ot_hours: number;  // Weekday overtime hours
        regular_pay: number;  // Non-overtime pay
        weekend_ot_pay: number;  // Weekend overtime pay 
        weekday_ot_pay: number;  // Weekday overtime pay
        total_regular_ppv: number;  // Total PPV earnings for this set
        total_ot_ppv: number;  // Total OT PPV earnings for this set
        nonppv: number;  // Total non-PPV earnings for this set
    }>;
    shifts: ShiftData[];
    bonus?: {
        total: number;
        set_totals: Record<string, { total: number }>;
    };
}

export const isUKTaxPayer = (userTaxCountry: string) => {
    return userTaxCountry === 'United Kingdom';
}

export const makeShiftData = (shift: Shift, pay_due: number): ShiftData => {
    return {
        shift_id: shift.id,
        user_id: shift.user_id!,
        model_id: shift.model_id,
        date: shift.start_date_time,
        shift_type: shift.shift_code,
        hours: shift.end_date_time.diff(shift.start_date_time, "hours").hours,
        ppv_earned: shift.ppv_earned,
        noppv_earned: shift.noppv_earned,
        pay_due: pay_due
    }
}

export const getModelSetById = (model_id: string, models: Model[]): number => {
    const model = models.find(m => m.id === model_id);
    return model?.set || 0;
};

// Aggregate ShiftData by model sets
export const aggregateShiftDataBySets = (shiftData: ShiftData[], models: Model[], bonus: number = 0, exchangeRate: number = 1): PayCalculatorResult => {
    // Use Map to track set totals directly in target format
    const setTotalsMap = new Map<string, {
        total: number;
        regular_hours: number;
        weekend_ot_hours: number;
        weekday_ot_hours: number;
        regular_pay: number;
        weekend_ot_pay: number;
        weekday_ot_pay: number;
        total_regular_ppv: number;
        total_ot_ppv: number;
        nonppv: number;
    }>();

    // Track regular pay by weekend/weekday for summary calculations
    let totalRegularWeekdayPay = 0;
    let totalRegularWeekendPay = 0;

    shiftData.forEach(shift => {
        const setId = getModelSetById(shift.model_id, models).toString();

        // Initialize set data if not exists
        if (!setTotalsMap.has(setId)) {
            setTotalsMap.set(setId, {
                total: 0,
                regular_hours: 0,
                weekend_ot_hours: 0,
                weekday_ot_hours: 0,
                regular_pay: 0,
                weekend_ot_pay: 0,
                weekday_ot_pay: 0,
                total_regular_ppv: 0,
                total_ot_ppv: 0,
                nonppv: 0
            });
        }

        const setData = setTotalsMap.get(setId)!;

        const day_of_week = getAdjustedDayOfWeek(shift.date);
        const isWeekend = day_of_week === 6 || day_of_week === 7;

        // Check if it's an OT shift using shift code
        const is_ot = OT_SHIFT_CODES_KEYS.includes(shift.shift_type);

        // Add to appropriate categories
        if (is_ot) {
            // Overtime shift - categorize by weekend/weekday based on actual date
            if (isWeekend) {
                setData.weekend_ot_hours += shift.hours;
                setData.weekend_ot_pay += shift.pay_due;
            } else {
                setData.weekday_ot_hours += shift.hours;
                setData.weekday_ot_pay += shift.pay_due;
            }
            setData.total_ot_ppv += shift.ppv_earned;
        } else {
            // Regular shift (all non-OT hours go into regular_hours regardless of weekend/weekday)
            setData.regular_hours += shift.hours;
            setData.regular_pay += shift.pay_due;
            setData.total_regular_ppv += shift.ppv_earned;

            // Track regular pay by weekend/weekday for summary calculations
            if (isWeekend) {
                totalRegularWeekendPay += shift.pay_due;
            } else {
                totalRegularWeekdayPay += shift.pay_due;
            }
        }

        // Always add non-PPV earnings
        setData.nonppv += shift.noppv_earned;

        // Update total pay (base pay only, bonuses added separately)
        setData.total = setData.regular_pay + setData.weekend_ot_pay + setData.weekday_ot_pay;
    });

    // Apply bonuses if bonus > 0 and track bonus amounts
    const bonusMap = new Map<string, { total: number }>();
    let totalBonusAmount = 0;

    if (bonus > 0) {
        setTotalsMap.forEach((setData, setId) => {
            // Calculate bonuses for shift pay
            let shiftBonus = 0;
            let otBonus = 0;

            // Shift bonus is a percentage of total_regular_ppv earnings
            shiftBonus = bonus * setData.total_regular_ppv;
            // OT bonus is ALWAYS 1% of total_ot_ppv earnings if bonus is > 0
            otBonus = 0.01 * setData.total_ot_ppv;

            // Convert total bonus to pounds and cap at £500 per set
            let total_bonus_pay = (shiftBonus + otBonus) * exchangeRate;
            const maxBonusPerSet = 500; // £500 cap per set

            if (total_bonus_pay > maxBonusPerSet) {
                total_bonus_pay = maxBonusPerSet;
            }

            // Track bonus amount for this set
            bonusMap.set(setId, { total: total_bonus_pay });
            totalBonusAmount += total_bonus_pay;
        });
    }

    // Convert map to Record format
    const set_totals: Record<string, {
        total: number;
        regular_hours: number;
        weekend_ot_hours: number;
        weekday_ot_hours: number;
        regular_pay: number;
        weekend_ot_pay: number;
        weekday_ot_pay: number;
        total_regular_ppv: number;
        total_ot_ppv: number;
        nonppv: number;
    }> = {};

    setTotalsMap.forEach((setData, setId) => {
        set_totals[setId] = setData;
    });

    // Calculate summary totals
    const setTotalsArray = Array.from(setTotalsMap.values());
    const total_pay = setTotalsArray.reduce((sum, set) => sum + set.total, 0);
    const weekday_ot_pay = setTotalsArray.reduce((sum, set) => sum + set.weekday_ot_pay, 0);
    const weekend_ot_pay = setTotalsArray.reduce((sum, set) => sum + set.weekend_ot_pay, 0);
    const weekdayPay = totalRegularWeekdayPay + weekday_ot_pay;
    const weekendPay = totalRegularWeekendPay + weekend_ot_pay;
    const overtimePay = setTotalsArray.reduce((sum, set) => sum + set.weekend_ot_pay + set.weekday_ot_pay, 0);
    const nonOvertimePay = setTotalsArray.reduce((sum, set) => sum + set.regular_pay, 0);

    const result: PayCalculatorResult = {
        total_pay,
        weekdayPay,
        weekendPay,
        overtimePay,
        nonOvertimePay,
        is_approximation: false,
        set_totals,
        shifts: shiftData
    };

    // Add bonus section if bonuses were applied
    if (totalBonusAmount > 0) {
        const bonus_set_totals: Record<string, { total: number }> = {};
        bonusMap.forEach((bonusData, setId) => {
            bonus_set_totals[setId] = bonusData;
        });

        result.bonus = {
            total: totalBonusAmount,
            set_totals: bonus_set_totals
        };
    }

    return result;
};

// Helper function to merge hours and pay data for sets
export const mergeHours = (
    targetHours: Record<string, {
        regular_hours: number;
        weekend_ot_hours: number;
        weekday_ot_hours: number;
        regular_pay: number;
        weekend_ot_pay: number;
        weekday_ot_pay: number;
        total_regular_ppv: number;
        total_ot_ppv: number;
        nonppv: number;
    }>,
    sourceHours: Record<string, {
        regular_hours: number;
        weekend_ot_hours: number;
        weekday_ot_hours: number;
        regular_pay: number;
        weekend_ot_pay: number;
        weekday_ot_pay: number;
        total_regular_ppv: number;
        total_ot_ppv: number;
        nonppv: number;
    }>
): void => {
    Object.entries(sourceHours).forEach(([set, hours]) => {
        if (!targetHours[set]) {
            targetHours[set] = {
                regular_hours: 0,
                weekend_ot_hours: 0,
                weekday_ot_hours: 0,
                regular_pay: 0,
                weekend_ot_pay: 0,
                weekday_ot_pay: 0,
                total_regular_ppv: 0,
                total_ot_ppv: 0,
                nonppv: 0
            };
        }
        targetHours[set].regular_hours += hours.regular_hours;
        targetHours[set].weekend_ot_hours += hours.weekend_ot_hours;
        targetHours[set].weekday_ot_hours += hours.weekday_ot_hours;
        targetHours[set].regular_pay += hours.regular_pay;
        targetHours[set].weekend_ot_pay += hours.weekend_ot_pay;
        targetHours[set].weekday_ot_pay += hours.weekday_ot_pay;
        targetHours[set].total_regular_ppv += hours.total_regular_ppv;
        targetHours[set].total_ot_ppv += hours.total_ot_ppv;
        targetHours[set].nonppv += hours.nonppv;
    });
};

// Aggregate SchedulingShiftData by model sets for scheduling pay
export const aggregateSchedulingShiftDataBySets = (schedulingShiftData: SchedulingShiftData[], models: Model[]) => {
    // Use Map to track set totals
    const setTotalsMap = new Map<string, number>();

    // Track totals by shift type
    const shiftTypeTotals = {
        uk_posts: 0,
        fp_posts: 0,
        mass_dm: 0,
        fp_mass_dm: 0,
        spam_posts: 0
    };

    schedulingShiftData.forEach(shift => {
        const setId = getModelSetById(shift.model_id, models).toString();

        // Add to set total
        const currentTotal = setTotalsMap.get(setId) || 0;
        setTotalsMap.set(setId, currentTotal + shift.pay_due);

        // Add to shift type totals
        switch (shift.shift_type) {
            case 'UK_POSTS':
                shiftTypeTotals.uk_posts += shift.pay_due;
                break;
            case 'FP_POSTS':
                shiftTypeTotals.fp_posts += shift.pay_due;
                break;
            case 'MASS_DM':
                shiftTypeTotals.mass_dm += shift.pay_due;
                break;
            case 'FP_MASS_DM':
                shiftTypeTotals.fp_mass_dm += shift.pay_due;
                break;
            case 'SPAM_POSTS':
                shiftTypeTotals.spam_posts += shift.pay_due;
                break;
        }
    });

    // Convert to array and sort by set number
    const setPayments = Array.from(setTotalsMap.entries())
        .map(([set, total_pay]) => ({ set, total_pay }))
        .sort((a, b) => parseInt(a.set) - parseInt(b.set));

    const total_pay = schedulingShiftData.reduce((sum, shift) => sum + shift.pay_due, 0);

    return {
        total_pay,
        setPayments,
        uk_posts_pay: { total_pay: shiftTypeTotals.uk_posts },
        fp_posts_pay: { total_pay: shiftTypeTotals.fp_posts },
        mass_dm_pay: { total_pay: shiftTypeTotals.mass_dm },
        fp_mass_dm_pay: { total_pay: shiftTypeTotals.fp_mass_dm },
        spam_posts_pay: { total_pay: shiftTypeTotals.spam_posts }
    };
};

// Aggregate GG swap data by model sets (combines both shifts and basic swap entries)
export const aggregateGGSwapDataBySets = (
    ggSwapShifts: ShiftData[],
    basicSwapResults: { total_pay: number; setPayments: { set: string; total_pay: number }[] } | null,
    models: Model[]
) => {
    // Use Map to track set totals
    const setTotalsMap = new Map<string, number>();

    // Track totals by type
    const typeTotals = {
        gg_swap: 0,
        gg_swap_cover: 0,
        basic_swap: 0
    };

    // Process GG swap shifts
    ggSwapShifts.forEach(shift => {
        const setId = getModelSetById(shift.model_id, models).toString();

        // Add to set total
        const currentTotal = setTotalsMap.get(setId) || 0;
        setTotalsMap.set(setId, currentTotal + shift.pay_due);

        // Add to type totals
        switch (shift.shift_type) {
            case 'GG_SWAP':
                typeTotals.gg_swap += shift.pay_due;
                break;
            case 'GG_SWAP_COVER':
                typeTotals.gg_swap_cover += shift.pay_due;
                break;
        }
    });

    // Process basic swap results if provided
    if (basicSwapResults) {
        typeTotals.basic_swap = basicSwapResults.total_pay;

        basicSwapResults.setPayments.forEach(setPayment => {
            const currentTotal = setTotalsMap.get(setPayment.set) || 0;
            setTotalsMap.set(setPayment.set, currentTotal + setPayment.total_pay);
        });
    }

    // Convert to array and sort by set number
    const setPayments = Array.from(setTotalsMap.entries())
        .map(([set, total_pay]) => ({ set, total_pay }))
        .sort((a, b) => parseInt(a.set) - parseInt(b.set));

    const shifts_total = ggSwapShifts.reduce((sum, shift) => sum + shift.pay_due, 0);
    const total_pay = shifts_total + (basicSwapResults?.total_pay || 0);

    return {
        total_pay,
        setPayments,
        gg_swap_pay: { total_pay: typeTotals.gg_swap },
        gg_swap_cover_pay: { total_pay: typeTotals.gg_swap_cover },
        basic_swap_pay: { total_pay: typeTotals.basic_swap }
    };
};